/**
 * Parser Data to Fabric.js Converter
 * PARSER_DATA_STRUCTURE.md 형식의 데이터를 Fabric.js 객체로 변환
 */
import * as fabric from 'fabric';
import { toFabricValue, toFabricTextGradient, isFabricGradient, fromFabricTextGradient, hexToRgb, rgbToHex, parserToGradientConfig, gradientConfigToParser, hexToRgba, parseColorToHex, parseColorOpacity } from '../utils/gradientUtils';
import { DEFAULT_TEXT_INSET } from '../constants/editorConstants';
import { setNoopLayoutManager, recalcTableCells } from '../utils/tableUtils';
import { createChartFabricImage } from '../utils/chartUtils';
import { applyLineControls } from '../utils/lineUtils';
import { createFrameClipPath } from '../utils/frameUtils';

/**
 * 투명도 변환 (0-100 → 0-1)
 * @param {number} opacity - 0~100
 * @returns {number} - 0~1
 */
export const convertOpacity = (opacity) => {
  if (opacity === undefined || opacity === null) return 1;
  return Math.max(0, Math.min(1, opacity / 100));
};

/**
 * font_data_list → Fabric.js styles 객체 변환 (리치 텍스트)
 * 기본 스타일과 다른 run만 styles에 추가
 * @param {Array} fontDataList - 파서의 font_data_list
 * @param {Object} defaultStyle - 기본 스타일 { font, font_color, font_size, bold, italic, underline }
 * @returns {Object} Fabric.js styles 객체 { lineIndex: { charIndex: { ...style } } }
 */
const buildRichTextStyles = (fontDataList, defaultStyle) => {
  const styles = {};
  let lineIdx = 0;
  let charIdx = 0;

  for (const fd of fontDataList) {
    const text = (fd.text || '').replace(/\r/g, '\n');
    // 현재 run의 스타일
    const runFont = fd.font || defaultStyle.font;
    const runSize = fd.font_size ?? defaultStyle.font_size;
    const runColor = fd.font_color || defaultStyle.font_color;
    const runBold = fd.bold || false;
    const runItalic = fd.italic || false;
    const runUnderline = fd.underline || false;

    // 기본 스타일과 비교하여 다른 속성만 수집
    const diff = {};
    if (runFont !== defaultStyle.font) diff.fontFamily = runFont;
    if (runSize !== defaultStyle.font_size) diff.fontSize = runSize;
    if (rgbToHex(runColor) !== rgbToHex(defaultStyle.font_color)) diff.fill = rgbToHex(runColor);
    if (runBold !== defaultStyle.bold) diff.fontWeight = runBold ? 'bold' : 'normal';
    if (runItalic !== defaultStyle.italic) diff.fontStyle = runItalic ? 'italic' : 'normal';
    if (runUnderline !== defaultStyle.underline) diff.underline = runUnderline;

    const hasDiff = Object.keys(diff).length > 0;

    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (ch === '\n') {
        lineIdx++;
        charIdx = 0;
        continue;
      }
      if (hasDiff) {
        if (!styles[lineIdx]) styles[lineIdx] = {};
        styles[lineIdx][charIdx] = { ...diff };
      }
      charIdx++;
    }
  }

  return styles;
};

/**
 * Fabric.js textbox → font_data_list 변환 (리치 텍스트 내보내기)
 * styles 객체를 읽어서 동일 스타일 연속 구간을 하나의 run으로 묶음
 * @param {fabric.Textbox} obj - Fabric.js 텍스트 객체
 * @param {Object} [overrides] - 텍스트박스 단위 속성 (has_gradient, gradient_data 등)
 * @returns {Array} font_data_list 배열
 */
const buildFontDataList = (obj, overrides = {}) => {
  const text = obj.text || '';
  const styles = obj.styles || {};
  const baseFont = obj.fontFamily || 'Noto Sans KR';
  const baseSize = obj.fontSize || 16;
  const baseFill = typeof obj.fill === 'string' ? obj.fill : '#000000';
  const baseRgb = hexToRgb(baseFill);
  const baseBold = obj.fontWeight === 'bold';
  const baseItalic = obj.fontStyle === 'italic';
  const baseUnderline = obj.underline || false;
  const baseOpacity = Math.round((obj.opacity ?? 1) * 100);

  // \n → \r 변환 (bullets가 있으면 indent는 \n 유지, 나머지는 \r)
  // bullets가 없으면 모든 \n → \r (paragraph break)
  const convertLineBreaks = (rawText) => {
    const lines = rawText.split('\n');
    if (lines.length <= 1) return rawText;
    let result = lines[0];
    for (let i = 1; i < lines.length; i++) {
      const b = obj._bullets?.[i];
      if (b && b.type === 'indent') {
        result += '\n' + lines[i]; // soft break (→ <a:br>)
      } else {
        result += '\r' + lines[i]; // paragraph break (→ <a:p>)
      }
    }
    return result;
  };

  // styles가 비어있으면 단일 run 반환
  if (!styles || Object.keys(styles).length === 0) {
    const fontData = {
      text: convertLineBreaks(text),
      font: baseFont, font_color: baseRgb, font_size: baseSize,
      bold: baseBold, italic: baseItalic, underline: baseUnderline,
      midline: false, opacity: baseOpacity, ...overrides,
    };
    return [fontData];
  }

  // 글자별 effective 스타일 계산 후 연속 동일 스타일 묶기
  const runs = [];
  let lineIdx = 0, charIdx = 0;
  let currentRun = null;

  const getCharStyle = (li, ci) => {
    const s = styles[li]?.[ci] || {};
    return {
      font: s.fontFamily || baseFont,
      font_size: s.fontSize ?? baseSize,
      font_color: s.fill ? hexToRgb(s.fill) : baseRgb,
      bold: s.fontWeight !== undefined ? s.fontWeight === 'bold' : baseBold,
      italic: s.fontStyle !== undefined ? s.fontStyle === 'italic' : baseItalic,
      underline: s.underline !== undefined ? s.underline : baseUnderline,
    };
  };

  const sameStyle = (a, b) =>
    a.font === b.font && a.font_size === b.font_size &&
    a.bold === b.bold && a.italic === b.italic && a.underline === b.underline &&
    a.font_color[0] === b.font_color[0] && a.font_color[1] === b.font_color[1] && a.font_color[2] === b.font_color[2];

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '\n') {
      // 줄바꿈은 현재 run에 추가 (나중에 \r 변환)
      if (currentRun) {
        currentRun.text += '\n';
      } else {
        currentRun = { text: '\n', ...getCharStyle(lineIdx, charIdx) };
      }
      lineIdx++;
      charIdx = 0;
      continue;
    }

    const style = getCharStyle(lineIdx, charIdx);
    if (currentRun && sameStyle(currentRun, style)) {
      currentRun.text += ch;
    } else {
      if (currentRun) runs.push(currentRun);
      currentRun = { text: ch, ...style };
    }
    charIdx++;
  }
  if (currentRun) runs.push(currentRun);

  // \n → \r 변환 적용 + 공통 속성 추가
  // 먼저 전체 텍스트에서 변환 맵 생성
  const converted = convertLineBreaks(text);
  let srcIdx = 0;
  for (const run of runs) {
    let newText = '';
    for (let j = 0; j < run.text.length; j++) {
      newText += converted[srcIdx];
      srcIdx++;
    }
    run.text = newText;
    run.midline = false;
    run.opacity = baseOpacity;
    if (overrides.has_gradient) {
      run.has_gradient = overrides.has_gradient;
      run.gradient_data = overrides.gradient_data;
    }
  }

  return runs;
};

/**
 * 이미지 레이어를 Fabric.js Image로 변환
 * @param {Object} layerData - 파서 이미지 데이터
 * @param {number} index - 레이어 인덱스
 * @returns {Promise<fabric.FabricImage>}
 */
/**
 * 원본 이미지에 마스크를 합성하여 data URL 반환
 * 마스크: 그레이스케일 이미지 (흰색=불투명, 검은색=투명)
 * @param {string} imageUrl - 원본 이미지 URL/data URL
 * @param {string} maskUrl - 마스크 이미지 URL/data URL
 * @returns {Promise<string>} 합성된 이미지 data URL
 */
const compositeWithMask = (imageUrl, maskUrl) => {
  return new Promise((resolve, reject) => {
    const origImg = new Image();
    const maskImg = new Image();
    let loaded = 0;

    const onBothLoaded = () => {
      const canvas = document.createElement('canvas');
      canvas.width = origImg.width;
      canvas.height = origImg.height;
      const ctx = canvas.getContext('2d');

      // 원본 이미지 그리기
      ctx.drawImage(origImg, 0, 0);

      // 마스크를 알파 채널로 적용
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = origImg.width;
      maskCanvas.height = origImg.height;
      const maskCtx = maskCanvas.getContext('2d');
      maskCtx.drawImage(maskImg, 0, 0, origImg.width, origImg.height);

      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const maskData = maskCtx.getImageData(0, 0, canvas.width, canvas.height);

      // 마스크의 밝기를 원본 알파에 적용
      for (let i = 0; i < imgData.data.length; i += 4) {
        imgData.data[i + 3] = maskData.data[i]; // R 채널을 알파로 사용
      }
      ctx.putImageData(imgData, 0, 0);

      resolve(canvas.toDataURL('image/png'));
    };

    origImg.onload = () => { if (++loaded === 2) onBothLoaded(); };
    maskImg.onload = () => { if (++loaded === 2) onBothLoaded(); };
    origImg.onerror = reject;
    maskImg.onerror = reject;
    origImg.crossOrigin = 'anonymous';
    maskImg.crossOrigin = 'anonymous';
    origImg.src = imageUrl;
    maskImg.src = maskUrl;
  });
};

export const convertImageLayer = async (layerData, index) => {
  const {
    offset = [0, 0],
    size = [100, 100],
    opacity = 100,
    rotation = 0,
    image_url,
    image_thumb_url,
    base64,
    mask_base64,
    mask_url,
    clip_info,
  } = layerData;

  // base64 → data URL, 또는 image_url 사용
  const imageUrl = base64
    ? `data:image/png;base64,${base64}`
    : (image_url || image_thumb_url);

  if (!imageUrl) {
    console.warn('이미지 URL이 없습니다:', layerData);
    return null;
  }

  try {
    // 마스크가 있으면 합성된 이미지 사용
    let finalUrl = imageUrl;
    const maskSrc = mask_base64
      ? `data:image/png;base64,${mask_base64}`
      : mask_url || null;

    if (maskSrc) {
      try {
        finalUrl = await compositeWithMask(imageUrl, maskSrc);
      } catch (e) {
        console.warn('마스크 합성 실패, 원본 사용:', e);
      }
    }

    const img = await fabric.FabricImage.fromURL(finalUrl, { crossOrigin: 'anonymous' });

    // 크기 계산 (스케일)
    const scaleX = size[0] / img.width;
    const scaleY = size[1] / img.height;

    img.set({
      left: offset[0] + size[0] / 2,
      top: offset[1] + size[1] / 2,
      originX: 'center',
      originY: 'center',
      scaleX: scaleX,
      scaleY: scaleY,
      angle: rotation,
      opacity: convertOpacity(opacity),
      id: `image-${Date.now()}-${index}`,
      name: `Image ${index + 1}`,
    });

    // 원본과 마스크 데이터를 보관 (마스크 편집용)
    if (maskSrc) {
      img._originalImageUrl = imageUrl;
      img._maskUrl = maskSrc;
      img._hasMask = true;
      img._maskApplied = true;
    }

    // clip_info가 있으면 프레임으로 변환
    if (clip_info && clip_info.rel_rect) {
      const [rx0, ry0, rx1, ry1] = clip_info.rel_rect;
      const frameShapeType = clip_info.type === 'ellipse' ? 'circle' : 'rect';

      // 원본 이미지 픽셀 크기
      const origW = img.width;
      const origH = img.height;

      // 클립 영역 (원본 이미지 픽셀 좌표)
      const cropX = Math.round(rx0 * origW);
      const cropY = Math.round(ry0 * origH);
      const cropW = Math.max(1, Math.round((rx1 - rx0) * origW));
      const cropH = Math.max(1, Math.round((ry1 - ry0) * origH));

      // 이미지를 클립 영역으로 자르기 (선택 박스 = 프레임 크기)
      img.set({ cropX, cropY, width: cropW, height: cropH });

      // 캔버스에서의 프레임 크기
      const frameW = size[0];
      const frameH = size[1];
      const newScaleX = frameW / cropW;
      const newScaleY = frameH / cropH;

      img.set({
        left: offset[0] + frameW / 2,
        top: offset[1] + frameH / 2,
        originX: 'center',
        originY: 'center',
        scaleX: newScaleX,
        scaleY: newScaleY,
        angle: rotation,
        opacity: convertOpacity(opacity),
      });

      // 비사각형 클립은 clipPath 추가
      if (frameShapeType !== 'rect') {
        const clipPath = createFrameClipPath(frameShapeType, cropW, cropH);
        img.clipPath = clipPath;
      }

      // 프레임 메타데이터 설정
      img._isFrame = true;
      img._isEmptyFrame = false;
      img._frameShapeType = frameShapeType;
      img._frameLocked = false;
      img._frameImageScale = newScaleX;
      img._frameWidth = frameW;
      img._frameHeight = frameH;
      img.id = `frame-${Date.now()}-${index}`;
      img.name = `Frame ${index + 1}`;
    }

    return img;
  } catch (error) {
    console.error('이미지 로드 오류:', error);
    return null;
  }
};

/**
 * 텍스트 레이어를 Fabric.js Textbox로 변환
 * word_wrap=true: 공백(단어) 단위 자동 줄바꿈 (PPT 동작 일치)
 * word_wrap=false: _wordJoiners 오버라이드로 줄바꿈 방지, dynamicMinWidth로 최소 너비 제한
 * @param {Object} layerData - 파서 텍스트 데이터
 * @param {number} index - 레이어 인덱스
 * @returns {fabric.Textbox}
 */
export const convertTextLayer = (layerData, index) => {
  const {
    offset = [0, 0],
    size = [200, 50],
    rotation = 0,
    'text-anchor': textAnchor = 'left',
    'vertical-align': verticalAlign = 'top',
    word_wrap = false,
    font_data_list = [],
    has_shadow = false,
    shadow_data,
    has_outline = false,
    outline_data,
    has_gradient = false,
    gradient_data,
    has_bg_fill = false,
    bg_color,
    bg_alpha = 100,
    bg_shape = 'rect',
  } = layerData;

  // 모든 텍스트 합치기
  const fullText = font_data_list.map(f => f.text || '').join('').replace(/\r/g, '\n');

  // 첫 번째 폰트 스타일 사용 (또는 기본값)
  const firstFont = font_data_list[0] || {};
  const {
    font = 'Noto Sans KR',
    font_color = [0, 0, 0],
    font_size = 16,
    bold = false,
    italic = false,
    underline = false,
    opacity: fontOpacity = 100,
  } = firstFont;

  // 리치 텍스트 스타일 생성
  const defaultStyle = { font, font_color, font_size, bold, italic, underline };
  const richStyles = buildRichTextStyles(font_data_list, defaultStyle);

  // PPT 프레임 중심 (회전 중심)
  const cx = offset[0] + size[0] / 2;
  const cy = offset[1] + size[1] / 2;

  // 그라데이션 데이터가 있으면 GradientConfig → fabric.Gradient, 없으면 단색
  let fillValue;
  if (has_gradient && gradient_data) {
    const gradientConfig = parserToGradientConfig(gradient_data);
    fillValue = toFabricValue(gradientConfig, size[0], size[1]);
  } else {
    fillValue = rgbToHex(font_color);
  }

  const overallOpacity = has_gradient && gradient_data
    ? convertOpacity(fontOpacity) * (gradient_data.overall_opacity ?? 1)
    : convertOpacity(fontOpacity);

  const commonProps = {
    left: cx,
    top: cy,
    originX: 'center',
    originY: 'center',
    fontFamily: font,
    fontSize: font_size,
    fill: fillValue,
    fontWeight: bold ? 'bold' : 'normal',
    fontStyle: italic ? 'italic' : 'normal',
    underline: underline,
    textAlign: textAnchor,
    lineHeight: layerData.line_height || 1.0,
    angle: rotation,
    opacity: overallOpacity,
    id: `text-${Date.now()}-${index}`,
    name: `Text ${index + 1}`,
  };

  // 내부 여백: 파서에서 전달된 값 우선, 없으면 기본값
  const marL = layerData._marL ?? DEFAULT_TEXT_INSET.L;
  const marR = layerData._marR ?? DEFAULT_TEXT_INSET.R;
  const marT = layerData._marT ?? DEFAULT_TEXT_INSET.T;
  const marB = layerData._marB ?? DEFAULT_TEXT_INSET.B;

  // 모두 Textbox로 생성, wordWrap에 따라 줄바꿈 동작 제어
  let textbox;
  const insetW = size[0] - marL - marR;

  const hasRichStyles = Object.keys(richStyles).length > 0;

  if (word_wrap) {
    textbox = new fabric.Textbox(fullText || '텍스트', {
      ...commonProps,
      width: insetW,
      splitByGrapheme: true,
      wordWrap: true,
      ...(hasRichStyles ? { styles: richStyles } : {}),
    });
  } else {
    textbox = new fabric.Textbox(fullText || '텍스트', {
      ...commonProps,
      width: insetW,
      wordWrap: false,
      ...(hasRichStyles ? { styles: richStyles } : {}),
    });
    textbox._wordJoiners = /\x00/;
    textbox.initDimensions();
  }

  // 내부 여백 속성 저장
  textbox._marL = marL;
  textbox._marR = marR;
  textbox._marT = marT;
  textbox._marB = marB;

  // 글머리 기호
  if (layerData.bullets) {
    textbox._bullets = layerData.bullets;
    textbox.initDimensions();
  }

  // 그라데이션이 있으면 실제 텍스트 너비에 맞게 재적용
  if (has_gradient && gradient_data) {
    const gradientConfig = parserToGradientConfig(gradient_data);
    textbox.set({ fill: toFabricTextGradient(gradientConfig, textbox) });
    textbox._gradientConfig = gradientConfig;
  }

  // 프레임 내 텍스트 중심 계산 (회전 전, 정렬 기반 + 여백 반영)
  let textCx = cx + (marL - marR) / 2;
  let textCy = cy + (marT - marB) / 2;

  // 수직 정렬
  if (verticalAlign === 'top') {
    textCy = offset[1] + marT + textbox.height / 2;
  } else if (verticalAlign === 'bottom') {
    textCy = offset[1] + size[1] - marB - textbox.height / 2;
  }

  // 수평 정렬 (wordWrap=false에서 width가 size[0]과 다를 때)
  if (!word_wrap) {
    if (textAnchor === 'left') {
      textCx = offset[0] + marL + textbox.width / 2;
    } else if (textAnchor === 'right') {
      textCx = offset[0] + size[0] - marR - textbox.width / 2;
    }
  }

  // 텍스트 중심을 프레임 중심 기준으로 회전 변환
  const rad = rotation * Math.PI / 180;
  const dx = textCx - cx;
  const dy = textCy - cy;
  textbox.set({
    left: cx + dx * Math.cos(rad) - dy * Math.sin(rad),
    top: cy + dx * Math.sin(rad) + dy * Math.cos(rad),
  });

  // 그림자 설정
  // 파서 각도: CW from North (0°=위, 90°=오른쪽, 180°=아래, 270°=왼쪽)
  // 화면 좌표: offsetX=sin(a)*d, offsetY=-cos(a)*d
  if (has_shadow && shadow_data) {
    const parserAngleRad = (shadow_data.angle || 135) * Math.PI / 180;
    const dist = shadow_data.dist || 3;
    textbox.shadow = new fabric.Shadow({
      color: `rgba(${parseInt(shadow_data.color?.slice(0, 2) || '00', 16)}, ${parseInt(shadow_data.color?.slice(2, 4) || '00', 16)}, ${parseInt(shadow_data.color?.slice(4, 6) || '00', 16)}, ${(shadow_data.opacity || 50) / 100})`,
      blur: shadow_data.blurRad || 4,
      offsetX: Math.sin(parserAngleRad) * dist,
      offsetY: -Math.cos(parserAngleRad) * dist,
    });
  }

  // 외곽선 설정 (Fabric.js에서는 stroke로 처리)
  if (has_outline && outline_data) {
    const outlineOpacity = (outline_data.opacity ?? 100) / 100;
    textbox.stroke = outlineOpacity < 1
      ? hexToRgba('#' + (outline_data.color || '000000'), outlineOpacity)
      : '#' + (outline_data.color || '000000');
    textbox.strokeWidth = outline_data.width || 1;
    textbox.paintFirst = 'stroke';
  }

  // 배경 채우기가 있으면 Rect + Textbox → Group으로 생성
  if (has_bg_fill && bg_color) {
    const bgHex = '#' + bg_color;
    const bgAlpha = (bg_alpha ?? 100) / 100;
    const roundShapes = ['roundRect', 'round1Rect', 'round2DiagRect', 'round2SameRect', 'snipRoundRect'];
    const cornerRadius = roundShapes.includes(bg_shape)
      ? Math.round(0.16667 * Math.min(size[0], size[1])) : 0;

    const bgFill = bgAlpha < 1
      ? `rgba(${parseInt(bgHex.slice(1, 3), 16)},${parseInt(bgHex.slice(3, 5), 16)},${parseInt(bgHex.slice(5, 7), 16)},${bgAlpha})`
      : bgHex;
    const borderOpacityVal = (layerData.border_opacity ?? 100) / 100;
    const borderColorHex = layerData.border_color ? '#' + layerData.border_color : null;
    const borderStroke = borderColorHex
      ? (borderOpacityVal < 1 ? `rgba(${parseInt(borderColorHex.slice(1, 3), 16)},${parseInt(borderColorHex.slice(3, 5), 16)},${parseInt(borderColorHex.slice(5, 7), 16)},${borderOpacityVal})` : borderColorHex)
      : null;
    const bgRect = new fabric.Rect({
      width: size[0], height: size[1],
      fill: bgFill, opacity: 1,
      rx: cornerRadius, ry: cornerRadius,
      originX: 'center', originY: 'center',
      selectable: false, evented: false,
      stroke: borderStroke,
      strokeWidth: layerData.border_width || 0,
    });

    // textbox 위치를 그룹 내부 좌표(중심 기준)로 변환
    const finalLeft = textbox.left;
    const finalTop = textbox.top;
    textbox.set({
      left: finalLeft - cx,
      top: finalTop - cy,
      originX: 'center',
      originY: 'center',
      angle: 0,  // 회전은 그룹에서 처리
    });

    const group = new fabric.Group([bgRect, textbox], {
      left: cx, top: cy, originX: 'center', originY: 'center',
      angle: rotation,
      subTargetCheck: false, interactive: false,
    });

    // 텍스트가 bgRect보다 클 경우 배경을 그룹 실제 크기에 맞춤
    const groupW = group.width;
    const groupH = group.height;
    if (groupW > size[0] || groupH > size[1]) {
      bgRect.set({ width: groupW, height: groupH });
    }

    // fabric v7: 생성자 옵션으로 커스텀 속성이 설정되지 않으므로 직접 할당
    group.id = `textboxgroup-${Date.now()}-${index}`;
    group.name = `Text ${index + 1}`;
    group._isTextBoxGroup = true;
    group._bgColor = bgHex;
    group._bgAlpha = bg_alpha;
    group._bgShape = bg_shape;
    group._borderColor = layerData.border_color ? '#' + layerData.border_color : null;
    group._borderOpacity = layerData.border_opacity ?? 100;
    group._borderWidth = layerData.border_width || 0;
    return group;
  }

  return textbox;
};

/**
 * 테이블 레이어를 Fabric.js Group(Rect[] + Textbox[])로 변환
 * @param {Object} layerData - 파서 테이블 데이터
 * @param {number} index - 레이어 인덱스
 * @returns {fabric.Group}
 */
export const convertTableLayer = (layerData, index) => {
  const {
    offset = [0, 0], size = [200, 100], rotation = 0, opacity = 100,
    rows = 1, cols = 1, col_widths = [], row_heights = [], cells = [],
  } = layerData;

  // 1-pass: Textbox를 먼저 생성하여 필요한 높이 측정
  const adjustedRowHeights = [...row_heights];
  const cellTextboxes = []; // { r, c, gridSpan, rowSpan, textbox, cellData }

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const cellData = cells[r]?.[c];
      if (!cellData) continue;
      if (cellData.hMerge || cellData.vMerge) continue;

      const gridSpan = cellData.gridSpan || 1;
      const rowSpan = cellData.rowSpan || 1;
      const cellW = col_widths.slice(c, c + gridSpan).reduce((a, b) => a + b, 0);
      const marL = cellData.marL ?? DEFAULT_TEXT_INSET.L;
      const marR = cellData.marR ?? DEFAULT_TEXT_INSET.R;
      const marT = cellData.marT ?? DEFAULT_TEXT_INSET.T;
      const marB = cellData.marB ?? DEFAULT_TEXT_INSET.B;

      const fontDataList = cellData.font_data_list || [];
      const fullText = fontDataList.map(f => f.text || '').join('').replace(/\r/g, '\n');
      const firstFont = fontDataList[0] || {};
      const cellDefaultStyle = {
        font: firstFont.font || 'Noto Sans KR',
        font_color: firstFont.font_color || [0, 0, 0],
        font_size: firstFont.font_size || 14,
        bold: firstFont.bold || false,
        italic: firstFont.italic || false,
        underline: firstFont.underline || false,
      };
      const cellStyles = buildRichTextStyles(fontDataList, cellDefaultStyle);
      const hasCellStyles = Object.keys(cellStyles).length > 0;

      const textbox = new fabric.Textbox(fullText || '', {
        width: cellW - marL - marR,
        fontFamily: cellDefaultStyle.font,
        fontSize: cellDefaultStyle.font_size,
        fontWeight: cellDefaultStyle.bold ? 'bold' : 'normal',
        fontStyle: cellDefaultStyle.italic ? 'italic' : 'normal',
        lineHeight: cellData.line_height || 1.0,
        splitByGrapheme: true,
        wordWrap: true,
        ...(hasCellStyles ? { styles: cellStyles } : {}),
      });

      // 텍스트 높이가 셀 높이를 초과하면 행 높이 확장 (rowSpan=1인 경우만)
      const textH = textbox.height || 0;
      const cellH = row_heights.slice(r, r + rowSpan).reduce((a, b) => a + b, 0);
      if (rowSpan === 1 && textH + marT + marB > cellH) {
        adjustedRowHeights[r] = Math.max(adjustedRowHeights[r], textH + marT + marB);
      }

      cellTextboxes.push({ r, c, gridSpan, rowSpan, cellData });
    }
  }

  // 2-pass: 조정된 높이로 최종 객체 생성
  const tableW = col_widths.reduce((a, b) => a + b, 0);
  const tableH = adjustedRowHeights.reduce((a, b) => a + b, 0);
  const cx = offset[0] + tableW / 2;
  const cy = offset[1] + tableH / 2;
  const fabricObjects = [];

  for (const { r, c, gridSpan, rowSpan, cellData } of cellTextboxes) {
    const cellX = col_widths.slice(0, c).reduce((a, b) => a + b, 0);
    const cellY = adjustedRowHeights.slice(0, r).reduce((a, b) => a + b, 0);
    const cellW = col_widths.slice(c, c + gridSpan).reduce((a, b) => a + b, 0);
    const cellH = adjustedRowHeights.slice(r, r + rowSpan).reduce((a, b) => a + b, 0);
    const localCx = cellX + cellW / 2 - tableW / 2;
    const localCy = cellY + cellH / 2 - tableH / 2;

    // 셀 마진
    const marL = cellData.marL ?? DEFAULT_TEXT_INSET.L;
    const marR = cellData.marR ?? DEFAULT_TEXT_INSET.R;
    const marT = cellData.marT ?? DEFAULT_TEXT_INSET.T;
    const marB = cellData.marB ?? DEFAULT_TEXT_INSET.B;

    // 셀 배경 Rect (테두리는 별도 Line으로 처리)
    const bgHex = '#' + (cellData.bg_color || 'FFFFFF');
    const bgAlpha = (cellData.bg_alpha ?? 100) / 100;
    const borders = cellData.borders || {};
    const fallbackColor = cellData.border_color ? '#' + cellData.border_color : '#000000';
    const fallbackWidth = cellData.border_width || 1;

    fabricObjects.push(new fabric.Rect({
      width: cellW, height: cellH,
      fill: bgHex, opacity: bgAlpha,
      stroke: null, strokeWidth: 0,
      left: localCx, top: localCy,
      originX: 'center', originY: 'center',
      selectable: false, evented: false,
      _cellRow: r, _cellCol: c, _isTableCell: true,
      _gridSpan: gridSpan, _rowSpan: rowSpan,
      _marL: marL, _marR: marR, _marT: marT, _marB: marB,
      _borders: borders,
      _origStroke: fallbackColor, _origStrokeWidth: fallbackWidth,
    }));

    // 4방향 테두리 Line 생성
    const x1 = localCx - cellW / 2, y1 = localCy - cellH / 2;
    const x2 = localCx + cellW / 2, y2 = localCy + cellH / 2;
    const borderDefs = [
      { key: 'T', coords: [x1, y1, x2, y1] },
      { key: 'B', coords: [x1, y2, x2, y2] },
      { key: 'L', coords: [x1, y1, x1, y2] },
      { key: 'R', coords: [x2, y1, x2, y2] },
    ];
    for (const { key, coords } of borderDefs) {
      const b = borders[key];
      if (b && b.width > 0) {
        fabricObjects.push(new fabric.Line(coords, {
          stroke: '#' + (b.color || '000000'),
          strokeWidth: b.width,
          selectable: false, evented: false,
          _isTableBorder: true,
          _cellRow: r, _cellCol: c, _borderDir: key,
        }));
      }
    }
    const fontDataList = cellData.font_data_list || [];
    const fullText = fontDataList.map(f => f.text || '').join('').replace(/\r/g, '\n');
    const firstFont = fontDataList[0] || {};
    const cellDef = {
      font: firstFont.font || 'Noto Sans KR',
      font_color: firstFont.font_color || [0, 0, 0],
      font_size: firstFont.font_size || 14,
      bold: firstFont.bold || false,
      italic: firstFont.italic || false,
      underline: firstFont.underline || false,
    };
    const cellRichStyles = buildRichTextStyles(fontDataList, cellDef);
    const hasCellRich = Object.keys(cellRichStyles).length > 0;
    // 마진에 따른 텍스트 위치 오프셋 (좌우 마진 차이 → 수평, 상하 마진 차이 → 수직)
    const textOffX = (marL - marR) / 2;
    const textOffY = (marT - marB) / 2;

    // 셀 텍스트 그라디언트 확인 (첫 번째 font_data의 그라디언트 사용)
    const cellHasGradient = firstFont.has_gradient && firstFont.gradient_data;
    const cellFill = cellHasGradient ? rgbToHex(cellDef.font_color) : rgbToHex(cellDef.font_color);

    fabricObjects.push(new fabric.Textbox(fullText || '', {
      width: cellW - marL - marR,
      left: localCx + textOffX, top: localCy + textOffY,
      originX: 'center', originY: 'center',
      fontFamily: cellDef.font,
      fontSize: cellDef.font_size,
      fill: cellFill,
      fontWeight: cellDef.bold ? 'bold' : 'normal',
      fontStyle: cellDef.italic ? 'italic' : 'normal',
      underline: cellDef.underline,
      lineHeight: cellData.line_height || 1.0,
      textAlign: cellData['text-anchor'] || 'left',
      ...(hasCellRich ? { styles: cellRichStyles } : {}),
      splitByGrapheme: true,
      wordWrap: true,
      selectable: true, evented: true,
      _cellRow: r, _cellCol: c, _isTableCellText: true,
      _gridSpan: gridSpan, _rowSpan: rowSpan,
      ...(cellData.bullets ? { _bullets: cellData.bullets } : {}),
    }));
    const cellTextbox = fabricObjects[fabricObjects.length - 1];
    // 셀 텍스트 그라디언트 적용
    if (cellHasGradient) {
      const gradConfig = parserToGradientConfig(firstFont.gradient_data);
      cellTextbox.set({ fill: toFabricTextGradient(gradConfig, cellTextbox) });
      cellTextbox._gradientConfig = gradConfig;
    }
    if (cellData.has_shadow && cellData.shadow_data) {
      const sd = cellData.shadow_data;
      const aRad = (sd.angle || 135) * Math.PI / 180;
      const dist = sd.dist || 3;
      cellTextbox.shadow = new fabric.Shadow({
        color: `rgba(${parseInt(sd.color?.slice(0, 2) || '00', 16)}, ${parseInt(sd.color?.slice(2, 4) || '00', 16)}, ${parseInt(sd.color?.slice(4, 6) || '00', 16)}, ${(sd.opacity || 50) / 100})`,
        blur: sd.blurRad || 4,
        offsetX: Math.sin(aRad) * dist,
        offsetY: -Math.cos(aRad) * dist,
      });
    }
    if (cellData.has_outline && cellData.outline_data) {
      const ol = cellData.outline_data;
      const olOpacity = (ol.opacity ?? 100) / 100;
      cellTextbox.stroke = olOpacity < 1
        ? hexToRgba('#' + (ol.color || '000000'), olOpacity)
        : '#' + (ol.color || '000000');
      cellTextbox.strokeWidth = ol.width || 1;
      cellTextbox.paintFirst = 'stroke';
    }
  }

  if (fabricObjects.length === 0) return null;

  const group = new fabric.Group(fabricObjects, {
    left: cx, top: cy, originX: 'center', originY: 'center',
    angle: rotation, opacity: convertOpacity(opacity),
    subTargetCheck: true, interactive: false,
  });
  // fabric v7: 생성자 옵션으로 커스텀 속성이 설정되지 않으므로 직접 할당
  group.id = `table-${Date.now()}-${index}`;
  group.name = `Table ${index + 1}`;
  group._isTableGroup = true;
  group._tableRows = rows;
  group._tableCols = cols;
  group._tableColWidths = col_widths;
  group._tableRowHeights = adjustedRowHeights;
  setNoopLayoutManager(group);
  // FitContentLayout이 시프트한 자식 위치를 올바르게 재배치
  recalcTableCells(group);
  return group;
};

/**
 * 도형 레이어를 Fabric.js 객체로 변환
 * @param {Object} layerData - 파서 도형 데이터
 * @param {number} index - 레이어 인덱스
 * @returns {fabric.FabricObject}
 */
export const convertShapeLayer = (layerData, index) => {
  const {
    shapeType,
    offset = [0, 0],
    size = [100, 100],
    fill,
    stroke,
    strokeWidth = 0,
    opacity = 100,
    rotation = 0,
    rx = 0,
    ry = 0,
    d,  // path의 d 속성
    flipH = false,
    flipV = false,
    has_gradient = false,
    gradient_data,
  } = layerData;

  // 그라데이션 데이터가 있으면 GradientConfig → fabric.Gradient
  let fillValue;
  if (has_gradient && gradient_data) {
    fillValue = toFabricValue(parserToGradientConfig(gradient_data), size[0], size[1]);
  } else {
    fillValue = fill || 'transparent';
  }

  const commonProps = {
    originX: 'center',
    originY: 'center',
    fill: fillValue,
    stroke: stroke || null,
    strokeWidth: strokeWidth,
    angle: rotation,
    opacity: convertOpacity(opacity),
    id: `shape-${Date.now()}-${index}`,
    name: `Shape ${index + 1}`,
  };

  let shape;

  if (shapeType === 'rect') {
    shape = new fabric.Rect({
      ...commonProps,
      left: offset[0] + size[0] / 2,
      top: offset[1] + size[1] / 2,
      width: size[0],
      height: size[1],
      rx: rx,
      ry: ry,
    });
  } else if (shapeType === 'ellipse') {
    shape = new fabric.Ellipse({
      ...commonProps,
      left: offset[0] + size[0] / 2,
      top: offset[1] + size[1] / 2,
      rx: size[0] / 2,
      ry: size[1] / 2,
    });
  } else if (shapeType === 'line' && d) {
    // 파서의 SVG path "M x1,y1 L x2,y2" → fabric.Line 좌표로 변환
    const coords = d.match(/[-\d.]+/g);
    if (coords && coords.length >= 4) {
      const x1 = offset[0] + parseFloat(coords[0]);
      const y1 = offset[1] + parseFloat(coords[1]);
      const x2 = offset[0] + parseFloat(coords[2]);
      const y2 = offset[1] + parseFloat(coords[3]);
      shape = new fabric.Line([x1, y1, x2, y2], {
        ...commonProps,
        fill: null,
        id: `line-${Date.now()}-${index}`,
        name: `Line ${index + 1}`,
        _marker: 'none',
      });
      applyLineControls(shape);
    }
  } else if (shapeType === 'path' && d) {
    // 앵커 포인트 추가: BB가 최소 [0,0]-[size] 영역을 포함하도록 강제
    // 이렇게 해야 텍스트(offset+size 기준)와 도형(BB 기준)의 위치가 일치
    const anchoredD = `M 0,0 M ${size[0]},${size[1]} ` + d;
    shape = new fabric.Path(anchoredD, {
      ...commonProps,
      left: offset[0] + size[0] / 2,
      top: offset[1] + size[1] / 2,
    });

    // Path가 예상 크기를 벗어나면 (파서 좌표 오류) 스케일로 보정
    if (Math.abs(shape.width - size[0]) > 1 || Math.abs(shape.height - size[1]) > 1) {
      shape.set({
        scaleX: size[0] / shape.width,
        scaleY: size[1] / shape.height,
      });
    }
  } else {
    console.warn('알 수 없는 도형 타입:', shapeType);
    return null;
  }

  // flip 처리
  if (flipH) {
    shape.set({ flipX: true });
  }
  if (flipV) {
    shape.set({ flipY: true });
  }

  return shape;
};

/**
 * 차트 레이어를 Fabric.js Image로 변환
 * Chart.js로 오프스크린 렌더링 후 fabric.Image로 래핑
 * @param {Object} layerData - 파서 차트 데이터
 * @param {number} index - 레이어 인덱스
 * @returns {Promise<fabric.FabricImage>}
 */
export const convertChartLayer = async (layerData, index) => {
  const {
    offset = [0, 0],
    size = [400, 300],
    opacity = 100,
    rotation = 0,
  } = layerData;

  const width = size[0];
  const height = size[1];

  const img = await createChartFabricImage(layerData, width, height);

  img.set({
    left: offset[0] + width / 2,
    top: offset[1] + height / 2,
    originX: 'center',
    originY: 'center',
    angle: rotation,
    opacity: convertOpacity(opacity),
    id: `chart_${Date.now()}_${index}`,
    name: layerData.title || `차트 ${index + 1}`,
  });

  return img;
};

/**
 * 단일 레이어 데이터를 Fabric.js 객체로 변환
 * @param {Object} layerData
 * @param {number} index
 * @returns {Promise<fabric.FabricObject | null>}
 */
export const convertLayer = async (layerData, index) => {
  if (!layerData || !layerData.type) {
    console.warn('잘못된 레이어 데이터:', layerData);
    return null;
  }

  switch (layerData.type) {
    case 'image':
      return convertImageLayer(layerData, index);
    case 'text':
      return convertTextLayer(layerData, index);
    case 'shape':
      return convertShapeLayer(layerData, index);
    case 'table':
      return convertTableLayer(layerData, index);
    case 'chart':
      return convertChartLayer(layerData, index);
    case 'group':
      return convertGroupLayer(layerData, index);
    default:
      console.warn('알 수 없는 레이어 타입:', layerData.type);
      return null;
  }
};

/**
 * 그룹 레이어를 Fabric.js Group으로 변환 (재귀)
 * @param {Object} layerData - 파서 그룹 데이터
 * @param {number} index - 레이어 인덱스
 * @returns {Promise<fabric.Group>}
 */
export const convertGroupLayer = async (layerData, index) => {
  const children = layerData.children || [];
  const fabricObjects = [];

  for (let i = 0; i < children.length; i++) {
    const obj = await convertLayer(children[i], i);
    if (obj) {
      fabricObjects.push(obj);
    }
  }

  if (fabricObjects.length === 0) return null;

  const group = new fabric.Group(fabricObjects, {
    subTargetCheck: false,
    interactive: false,
  });
  // fabric v7: 생성자 옵션으로 커스텀 속성이 설정되지 않으므로 직접 할당
  group.id = `group-${Date.now()}-${index}`;
  group.name = `Group ${index + 1}`;
  group._layerExpanded = true;

  return group;
};

/**
 * 슬라이드 데이터를 Fabric.js 객체 배열로 변환
 * @param {Array} slideData - 레이어 배열
 * @returns {Promise<fabric.FabricObject[]>}
 */
export const convertSlide = async (slideData) => {
  const objects = [];

  for (let i = 0; i < slideData.length; i++) {
    const obj = await convertLayer(slideData[i], i);
    if (obj) {
      objects.push(obj);
    }
  }

  return objects;
};

/**
 * Fabric.js 객체를 파서 데이터 형식으로 변환 (내보내기용)
 * @param {fabric.FabricObject} obj
 * @returns {Object}
 */
export const fabricToParserFormat = (obj) => {
  // TextBoxGroup: 텍스트로 내보내기 (배경 데이터 포함)
  if (obj.type === 'group' && obj._isTextBoxGroup) {
    const textChild = obj.getObjects().find(c => ['textbox', 'i-text', 'text'].includes(c.type));
    if (textChild) {
      const result = fabricToParserFormat(textChild);
      // 위치/크기를 그룹 기준으로 오버라이드
      const center = obj.getCenterPoint();
      const w = (obj.width || 100) * (obj.scaleX || 1);
      const h = (obj.height || 100) * (obj.scaleY || 1);
      result.offset = [center.x - w / 2, center.y - h / 2];
      result.size = [w, h];
      result.rotation = obj.angle || 0;
      // 배경 데이터 추가
      result.has_bg_fill = true;
      result.bg_color = (obj._bgColor || '#ffffff').replace('#', '');
      result.bg_alpha = obj._bgAlpha ?? 100;
      result.bg_shape = obj._bgShape || 'rect';
      // 테두리 데이터 추가
      if (obj._borderColor) {
        result.border_color = obj._borderColor.replace('#', '');
        result.border_opacity = obj._borderOpacity ?? 100;
        result.border_width = obj._borderWidth || 1;
      }
      return result;
    }
  }

  // 테이블 그룹: table 형식으로 역변환
  if (obj.type === 'group' && obj._isTableGroup) {
    const center = obj.getCenterPoint();
    const w = (obj.width || 100) * (obj.scaleX || 1);
    const h = (obj.height || 100) * (obj.scaleY || 1);
    const rows = obj._tableRows, cols = obj._tableCols;

    // 스케일 반영한 col_widths, row_heights
    const scaleX = obj.scaleX || 1, scaleY = obj.scaleY || 1;
    const colWidths = (obj._tableColWidths || []).map(v => Math.round(v * scaleX));
    const rowHeights = (obj._tableRowHeights || []).map(v => Math.round(v * scaleY));

    // 병합 맵 구성: 병합 시작 셀의 gridSpan/rowSpan으로 가려진 셀 위치 파악
    const mergeMap = {}; // `${r}_${c}` → { type: 'start', gridSpan, rowSpan } 또는 { type: 'hMerge' } 또는 { type: 'vMerge' }
    const allObjects = obj.getObjects();
    allObjects.forEach(ch => {
      if (ch._isTableCell && (ch._gridSpan > 1 || ch._rowSpan > 1)) {
        const gr = ch._gridSpan || 1, rs = ch._rowSpan || 1;
        for (let dr = 0; dr < rs; dr++) {
          for (let dc = 0; dc < gr; dc++) {
            if (dr === 0 && dc === 0) continue;
            const key = `${ch._cellRow + dr}_${ch._cellCol + dc}`;
            mergeMap[key] = dc > 0 && dr === 0 ? { type: 'hMerge' }
              : { type: 'vMerge' };
          }
        }
      }
    });

    const cells = [];
    for (let r = 0; r < rows; r++) {
      const rowData = [];
      for (let c = 0; c < cols; c++) {
        const mergeInfo = mergeMap[`${r}_${c}`];
        if (mergeInfo) {
          // 병합으로 가려진 셀: 최소 데이터만 출력
          rowData.push({
            row: r, col: c,
            'text-anchor': 'left', 'vertical-align': 'center',
            bg_color: 'FFFFFF', bg_alpha: 100,
            border_color: '000000', border_width: 1,
            font_data_list: [],
            gridSpan: 1, rowSpan: 1,
            hMerge: mergeInfo.type === 'hMerge',
            vMerge: mergeInfo.type === 'vMerge',
          });
          continue;
        }

        const textChild = allObjects.find(
          ch => ch._isTableCellText && ch._cellRow === r && ch._cellCol === c
        );
        const rectChild = allObjects.find(
          ch => ch._isTableCell && ch._cellRow === r && ch._cellCol === c
        );
        rowData.push({
          row: r, col: c,
          'text-anchor': textChild?.textAlign || 'left',
          'vertical-align': 'center',
          bg_color: (rectChild?.fill || '#FFFFFF').replace('#', ''),
          bg_alpha: Math.round((rectChild?.opacity ?? 1) * 100),
          border_color: (rectChild?.stroke || '#000000').replace('#', ''),
          border_width: rectChild?.strokeWidth || 1,
          font_data_list: textChild ? buildFontDataList(textChild) : [],
          gridSpan: rectChild?._gridSpan || 1,
          rowSpan: rectChild?._rowSpan || 1,
          hMerge: false,
          vMerge: false,
          marL: rectChild?._marL ?? DEFAULT_TEXT_INSET.L,
          marR: rectChild?._marR ?? DEFAULT_TEXT_INSET.R,
          marT: rectChild?._marT ?? DEFAULT_TEXT_INSET.T,
          marB: rectChild?._marB ?? DEFAULT_TEXT_INSET.B,
          ...(textChild?._bullets ? { bullets: textChild._bullets } : {}),
          // 셀 텍스트는 그림자 미지원, 외곽선만 export
          has_outline: !!(textChild?.stroke && textChild?.strokeWidth > 0),
          ...(textChild?.stroke && textChild?.strokeWidth > 0 ? (() => {
            const op = typeof textChild.stroke === 'string' && textChild.stroke.startsWith('rgba')
              ? parseColorOpacity(textChild.stroke) : 1;
            const ch = parseColorToHex(typeof textChild.stroke === 'string' ? textChild.stroke : '#000000');
            return { outline_data: {
              color: ch.replace('#', ''),
              opacity: Math.round(op * 100),
              width: Math.round(textChild.strokeWidth),
            }};
          })() : {}),
        });
      }
      cells.push(rowData);
    }

    return {
      type: 'table',
      offset: [center.x - w / 2, center.y - h / 2],
      size: [w, h],
      rotation: obj.angle || 0,
      rows, cols,
      col_widths: colWidths,
      row_heights: rowHeights,
      cells,
    };
  }

  // 차트: 메타데이터로 역변환
  if (obj._isChart) {
    const center = obj.getCenterPoint();
    const w = (obj.width || 100) * (obj.scaleX || 1);
    const h = (obj.height || 100) * (obj.scaleY || 1);
    return {
      type: 'chart',
      offset: [center.x - w / 2, center.y - h / 2],
      size: [w, h],
      rotation: obj.angle || 0,
      opacity: Math.round((obj.opacity ?? 1) * 100),
      chartType: obj._chartType,
      chartSubType: obj._chartSubType,
      title: obj._chartTitle,
      titleFontSize: obj._chartTitleFontSize ?? null,
      titleColor: obj._chartTitleColor ?? null,
      series: typeof obj._chartSeries === 'string' ? JSON.parse(obj._chartSeries) : obj._chartSeries,
      axes: typeof obj._chartAxes === 'string' ? JSON.parse(obj._chartAxes) : obj._chartAxes,
      legend: obj._chartLegend ? (typeof obj._chartLegend === 'string' ? JSON.parse(obj._chartLegend) : obj._chartLegend) : null,
      dataPointColors: obj._chartDataPointColors ? (typeof obj._chartDataPointColors === 'string' ? JSON.parse(obj._chartDataPointColors) : obj._chartDataPointColors) : null,
      gapWidth: obj._chartGapWidth ?? null,
      overlap: obj._chartOverlap ?? null,
    };
  }

  // 그룹: 구조 유지하여 내보내기 (재귀)
  if (obj.type === 'group') {
    const center = obj.getCenterPoint();
    const w = (obj.width || 100) * (obj.scaleX || 1);
    const h = (obj.height || 100) * (obj.scaleY || 1);
    return {
      type: 'group',
      offset: [center.x - w / 2, center.y - h / 2],
      size: [w, h],
      opacity: Math.round((obj.opacity ?? 1) * 100),
      rotation: obj.angle || 0,
      children: obj.getObjects().map(child => fabricToParserFormat(child)),
    };
  }

  const center = obj.getCenterPoint();
  const w = (obj.width || 100) * (obj.scaleX || 1);
  const h = (obj.height || 100) * (obj.scaleY || 1);

  // 텍스트박스: margin을 복원하여 원본 프레임 크기로 내보내기
  const isText = obj.type === 'textbox' || obj.type === 'i-text' || obj.type === 'text';
  const marL = isText ? (obj._marL ?? DEFAULT_TEXT_INSET.L) : 0;
  const marR = isText ? (obj._marR ?? DEFAULT_TEXT_INSET.R) : 0;
  const marT = isText ? (obj._marT ?? DEFAULT_TEXT_INSET.T) : 0;
  const marB = isText ? (obj._marB ?? DEFAULT_TEXT_INSET.B) : 0;
  const frameW = w + marL + marR;
  const frameH = h + marT + marB;

  const baseData = {
    offset: [center.x - frameW / 2, center.y - frameH / 2],
    size: [frameW, frameH],
    opacity: Math.round((obj.opacity ?? 1) * 100),
    rotation: obj.angle || 0,
  };

  if (obj.type === 'image') {
    return {
      type: 'image',
      ...baseData,
      // base64는 서버에서 URL로부터 가져옴
      image_url: obj.getSrc?.() || '',
    };
  }

  if (obj.type === 'textbox' || obj.type === 'i-text' || obj.type === 'text') {
    // 텍스트박스 단위 그라디언트 처리
    const fill = obj.fill || '#000000';
    const gradientOverrides = {};

    if (obj._gradientConfig) {
      gradientOverrides.has_gradient = true;
      gradientOverrides.gradient_data = gradientConfigToParser(obj._gradientConfig);
    } else if (isFabricGradient(fill)) {
      const config = fromFabricTextGradient(fill, obj);
      gradientOverrides.has_gradient = true;
      gradientOverrides.gradient_data = gradientConfigToParser(config);
    }

    const fontDataList = buildFontDataList(obj, gradientOverrides);

    const result = {
      type: 'text',
      ...baseData,
      direction: true,
      'text-anchor': obj.textAlign || 'left',
      word_wrap: obj.wordWrap !== false,
      has_shadow: false,
      has_outline: false,
      font_data_list: fontDataList,
      _marL: obj._marL ?? DEFAULT_TEXT_INSET.L,
      _marR: obj._marR ?? DEFAULT_TEXT_INSET.R,
      _marT: obj._marT ?? DEFAULT_TEXT_INSET.T,
      _marB: obj._marB ?? DEFAULT_TEXT_INSET.B,
      line_height: obj.lineHeight ?? 1.0,
    };

    // 글머리 기호
    if (obj._bullets) {
      result.bullets = obj._bullets;
    }

    // Shadow 데이터 역변환
    if (obj.shadow) {
      const s = obj.shadow;
      const opacity = parseColorOpacity(s.color);
      const colorHex = parseColorToHex(s.color);
      result.has_shadow = true;
      // 화면 오프셋 → 파서 각도 (CW from North): atan2(offsetX, -offsetY)
      const angle = Math.round(Math.atan2(s.offsetX, -s.offsetY) * 180 / Math.PI);
      const dist = Math.round(Math.sqrt(s.offsetX ** 2 + s.offsetY ** 2));
      result.shadow_data = {
        color: colorHex.replace('#', ''),
        opacity: Math.round(opacity * 100),
        angle: ((angle % 360) + 360) % 360,
        dist,
        blurRad: Math.round(s.blur || 0),
      };
    }

    // Outline 데이터 역변환
    if (obj.stroke && obj.strokeWidth > 0) {
      const opacity = typeof obj.stroke === 'string' && obj.stroke.startsWith('rgba')
        ? parseColorOpacity(obj.stroke) : 1;
      const colorHex = parseColorToHex(typeof obj.stroke === 'string' ? obj.stroke : '#000000');
      result.has_outline = true;
      result.outline_data = {
        color: colorHex.replace('#', ''),
        opacity: Math.round(opacity * 100),
        width: Math.round(obj.strokeWidth),
      };
    }

    if (gradientOverrides.has_gradient) {
      result.has_gradient = true;
      result.gradient_data = gradientOverrides.gradient_data;
    }

    return result;
  }

  // 도형 → 이미지로 변환 필요 (서버에서 처리)
  return {
    type: 'image',
    ...baseData,
    // 도형은 캔버스에서 PNG로 렌더링하여 base64 생성 필요
  };
};

/**
 * 캔버스 전체를 파서 데이터 형식으로 변환
 * @param {fabric.Canvas} canvas
 * @returns {Object} - { canvas_size: [w, h], data_list: [[layer, ...]] }
 */
export const canvasToParserFormat = (canvas) => {
  const objects = canvas.getObjects();
  const layers = objects.map(obj => fabricToParserFormat(obj));

  return {
    canvas_size: [canvas.width, canvas.height],
    data_list: [layers], // 단일 슬라이드
  };
};
